import {
	type Login,
	type Recover,
	type Register,
	type RegisterResponse,
	type ResetPassword,
	registerResponseSchema,
	type SessionResponse,
	safeRedirect,
	sessionResponseSchema,
} from '@diario/common';
import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { type } from 'arktype';
import { env } from '../../../lib/env';
import { type ApiError, apiPost } from '../../../lib/http';
import { emitirCambioSesion } from './sesionCanal';
import { sesionQueryOptions } from './useSesion';

/**
 * `POST /auth/recover` y `POST /auth/logout` responden `200 sin cuerpo` (especificación de la
 * iteración, tabla «Endpoints de sesión»): no hay contrato de respuesta en `@diario/common`
 * porque no hay nada que validar. Mismo patrón que usa `http.test.ts` para un `DELETE` sin
 * cuerpo — `type('undefined')`, no un `type('unknown')` laxo.
 */
const sinCuerpoSchema = type('undefined');

/**
 * Entrar (`POST /auth/login`). Al terminar con éxito invalida la query de sesión (para que
 * `GET /me` se vuelva a pedir con la cookie ya puesta) y avisa al resto de pestañas — CL-4.
 */
export const useEntrar = (): UseMutationResult<SessionResponse, ApiError, Login> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (datos: Login) => apiPost('/auth/login', sessionResponseSchema, datos),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: sesionQueryOptions.queryKey });
			emitirCambioSesion('entrada');
		},
	});
};

/**
 * Crear cuenta (`POST /auth/register`). Con la confirmación por correo activada, el alta no
 * siempre crea sesión (`estado: 'confirmacionPendiente'`, documento 02b §10.1): solo se
 * invalida la query de sesión y se avisa a otras pestañas cuando `estado === 'sesionCreada'`,
 * nunca en el caso de confirmación pendiente, porque ahí no ha cambiado nada que comprobar.
 */
export const useCrearCuenta = (): UseMutationResult<RegisterResponse, ApiError, Register> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (datos: Register) => apiPost('/auth/register', registerResponseSchema, datos),
		onSuccess: async respuesta => {
			if (respuesta.estado === 'sesionCreada') {
				await queryClient.invalidateQueries({ queryKey: sesionQueryOptions.queryKey });
				emitirCambioSesion('entrada');
			}
		},
	});
};

/** Recuperar contraseña (`POST /auth/recover`). No toca la sesión: no hay nada que invalidar
 * ni que emitir a otras pestañas. */
export const useRecuperarContrasena = (): UseMutationResult<undefined, ApiError, Recover> =>
	useMutation({
		mutationFn: (datos: Recover) => apiPost('/auth/recover', sinCuerpoSchema, datos),
	});

/**
 * Restablecer contraseña (`POST /auth/reset`), canjeando el `tokenHash` del enlace de
 * recuperación por la contraseña nueva. Sin `onSuccess`: no hay sesión que invalidar ni cambio
 * que emitir a otras pestañas (CL-4 no aplica aquí) — el backend manda cookies de limpieza en la
 * propia respuesta, y quien complete este paso vuelve a `/login` a entrar desde cero con la
 * contraseña nueva, no sigue en la app con una sesión que este hook tendría que anunciar.
 */
export const useRestablecerContrasena = (): UseMutationResult<undefined, ApiError, ResetPassword> =>
	useMutation({ mutationFn: (datos: ResetPassword) => apiPost('/auth/reset', sinCuerpoSchema, datos) });

/**
 * Salir (`POST /auth/logout`). Avisa al resto de pestañas — es exactamente el caso límite CL-4
 * del cliente («cierra sesión en una pestaña, la otra se entera») — pero, a diferencia de
 * `useEntrar`/`useCrearCuenta`, `resetQueries` en vez de `invalidateQueries` (H-04/B8, «sesión
 * zombi»): tras invalidar, el refetch de `GET /me` recibe 401 de verdad, pero el reductor de
 * error de TanStack Query CONSERVA el `data` que ya hubiera en caché en vez de vaciarlo, así que
 * `ensureQueryData` (el `beforeLoad` de `_autenticado.tsx`, `index.tsx` y `centro.tsx`) seguía
 * devolviendo la sesión vieja sin comprobar si el refetch de fondo había fallado —
 * `page.goBack()` tras cerrar sesión enseñaba de nuevo la portada con sesión. `resetQueries` vacía
 * `data` a `undefined` de inmediato (no solo lo marca obsoleto), notifica a los `useQuery`
 * suscritos (la cabecera, vía `useSesion` en `__root.tsx`) y relanza el refetch: la próxima
 * comprobación de sesión ya no tiene ningún dato cacheado que devolver de más.
 */
export const useSalir = (): UseMutationResult<undefined, ApiError, void> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => apiPost('/auth/logout', sinCuerpoSchema),
		onSuccess: async () => {
			await queryClient.resetQueries({ queryKey: sesionQueryOptions.queryKey });
			emitirCambioSesion('salida');
		},
	});
};

/**
 * El acceso con Google NO es una mutación de TanStack Query: `GET /auth/google` no devuelve
 * JSON, devuelve una redirección HTTP a la pantalla de consentimiento de Google (el backend
 * media todo el flujo OAuth con GoTrue — «Endpoints de sesión», especificación de la
 * iteración). Consumirlo es una navegación real del navegador (un `<a href>` normal, o
 * `window.location.assign`), nunca un `fetch`: por eso aquí no hay `useMutation` para Google,
 * solo este constructor de URL puro. `destino` pasa siempre por `safeRedirect` antes de entrar
 * en el enlace, para que un valor manipulado en el cliente no pueda sacar a la persona del
 * dominio ni disfrazar la ruta de OAuth.
 */
export const construirUrlAccesoGoogle = (destino?: string): string =>
	`${env.VITE_API_URL}/auth/google?redirect=${encodeURIComponent(safeRedirect(destino))}`;
